import { useCallback, useState } from 'react'
import { type AnalyticsEventPayloads, trackEvent } from '@/lib/analytics'
import { APP_CONFIG } from '@/lib/config/constants'
import { handleMutationError } from '@/lib/utils/errors'

export interface UseFormSubmissionOptions<TFields extends Record<string, unknown>> {
  /** Function to submit the form data */
  submitFn: (data: TFields) => Promise<void>
  /** Function to validate form data before submission */
  validateFn?: (data: TFields) => void
  /** Function to reset form fields */
  resetFn?: (prevData: TFields) => TFields
  /** Success callback after submission */
  onSuccess?: (data: TFields) => void
  /** Error callback if submission fails */
  onError?: (error: Error, data: TFields) => void
  /** Analytics event name for successful submission */
  analyticsEvent?: string
  /** Function to extract analytics properties from form data */
  analyticsProps?: (data: TFields) => Record<string, unknown>
  /** Timeout for auto-reset of submitted state */
  resetTimeout?: number
}

export interface UseFormSubmissionReturn<TFields extends Record<string, unknown>> {
  /** Current form data */
  data: TFields
  /** Update form data */
  setData: (data: Partial<TFields> | ((prev: TFields) => TFields)) => void
  /** Whether form is currently submitting */
  isSubmitting: boolean
  /** Whether form was successfully submitted (resets after timeout) */
  isSubmitted: boolean
  /** Any error that occurred during submission */
  error: string | null
  /** Submit the form */
  submit: (e?: React.FormEvent) => Promise<void>
  /** Reset form to initial state */
  reset: () => void
}

/**
 * Generic hook for form submission with loading states, error handling, and analytics
 */
export function useFormSubmission<TFields extends Record<string, unknown>>(
  initialData: TFields,
  options: UseFormSubmissionOptions<TFields>
): UseFormSubmissionReturn<TFields> {
  const [data, setDataState] = useState<TFields>(initialData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setData = useCallback((newData: Partial<TFields> | ((prev: TFields) => TFields)) => {
    if (typeof newData === 'function') {
      setDataState(newData)
    } else {
      setDataState((prev) => ({ ...prev, ...newData }))
    }
  }, [])

  const reset = useCallback(() => {
    setDataState(initialData)
    setIsSubmitting(false)
    setIsSubmitted(false)
    setError(null)
  }, [initialData])

  const submit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()

      try {
        // Validate if validator provided
        if (options.validateFn) {
          options.validateFn(data)
        }

        setIsSubmitting(true)
        setError(null)

        // Submit the form
        await options.submitFn(data)

        // Success state
        setIsSubmitted(true)
        setIsSubmitting(false)

        // Track analytics if provided
        if (options.analyticsEvent && options.analyticsProps) {
          const properties = options.analyticsProps(data)
          trackEvent(
            options.analyticsEvent as keyof AnalyticsEventPayloads,
            properties as Record<string, unknown>
          )
        }

        // Call success callback
        options.onSuccess?.(data)

        // Reset after timeout if specified
        const timeout = options.resetTimeout ?? APP_CONFIG.TOAST_DURATION
        if (timeout > 0) {
          setTimeout(() => {
            setIsSubmitted(false)
            // Also reset form data if reset function provided
            if (options.resetFn) {
              setDataState(options.resetFn(data))
            }
          }, timeout)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Submission failed'
        setError(errorMessage)
        setIsSubmitting(false)
        setIsSubmitted(false)

        // Call error callback
        options.onError?.(err instanceof Error ? err : new Error(errorMessage), data)

        // Handle error with toast
        handleMutationError(err, 'Form submission failed')
      }
    },
    [data, options]
  )

  return {
    data,
    setData,
    isSubmitting,
    isSubmitted,
    error,
    submit,
    reset,
  }
}
